import React, { useContext } from 'react';
import EventTeamTables from '../../inputs/EventTeamTables';
import TravelInformation from '../../inputs/TravelInformation';
import EventDates from '../../inputs/EventDates';
import ApolloError from '@shared/components/ApolloError';
import OrgOrgUnitSelection from '../../inputs/OrgOrgUnitSelection';
import { getToday } from '@shared/services/dateService';
import ChangeDateRequest from '../../../shared/ChangeDateRequest';
import { ATL_SELECTION_TYPE, EDITABLE_SELECTION_TYPES } from '../../../shared/constants';
import AtlSelection from '../../inputs/AtlSelection';
import useAvailableAtlList from '../../../../hooks/useAvailableAtlList';
import { useAtlSelectionType } from '../../../shared/hooks/useAtlSelectionType';
import EventStatusWarnings from '../EventStatusWarnings';
import { useExcludedDateIntervals } from '../../../shared/hooks/useExcludedDateIntervals';
import SowStatusSectionWrapper from '../SowStatus/SowStatusSectionWrapper';
import { EventSettingsContext } from '../../../shared/contexts/EventSettingsContext';
import AtlPreferenceTable from '../../inputs/AtlPreferenceTable';
import { EVENT_FLAGS } from '@features/calendar/constants/EventFlags';

/**
 * @typedef {object} AppraiserEditEventStoredEventData
 * @property {Object} [Atl]
 * @property {number} OrgId
 * @property {*} [Stage]
 * @property {Array<*>} WarningTypes
 * @property {string} [IntendedStartDate]
 * @property {Array<*>} EventFlags
 */

/**
 * @typedef {object} AppraiserEditEventProps
 * @property {number} eventId
 * @property {boolean} [eventLoading]
 * @property {Object} [eventFormData]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} setEventFormData
 * @property {(startDateName: string, endDateName: string) => (startDate: Date, endDate: Date) => void} getOnDateRangeChangedHandler
 * @property {Array<*>} [organizations]
 * @property {boolean} [orgUnitsLoading]
 * @property {Object} [validationErrors]
 * @property {boolean} [disableDateEdit]
 * @property {AppraiserEditEventStoredEventData} [storedEventData]
 * @property {*} [dateRequestStatus]
 * @property {(requestData: { startDate: Date | null, endDate: Date | null, comment: string }) => void | Promise<void>} handleSubmitDataRequest
 * @property {boolean} [isAtmView]
 * @property {Array<*>} [filteredAssignmentData]
 */

/**
 * @param {AppraiserEditEventProps} props
 */
const AppraiserEditEvent = props => {
  const {
    eventId,
    eventFormData,
    storedEventData,
    getOnDateRangeChangedHandler,
    organizations,
    filteredAssignmentData,
    validationErrors,
    setEventFormData,
    handleSubmitDataRequest,
    dateRequestStatus = null,
    orgUnitsLoading = false,
    disableDateEdit = false,
    isAtmView = false,
    eventLoading = false
  } = props;

  const settings = useContext(EventSettingsContext);
  const isEditDisabled = !settings.isEditEnabled || !settings.canEditBaseFields;

  const canUserChangeDateOrRequestDateChange = !isEditDisabled || settings.canRequestDateChange;

  const {
    data: excludedDateIntervals,
    error: appraiserUnavailableError,
    loading: appraiserUnavailableLoading
  } = useExcludedDateIntervals(eventId, !canUserChangeDateOrRequestDateChange);

  const atlSelectionType = useAtlSelectionType(eventLoading, storedEventData?.Stage, settings);

  const { availableAtlList, availableAtlsLoading, availableAtlsError } = useAvailableAtlList(
    eventFormData.startDate,
    eventFormData.endDate,
    null,
    null,
    setEventFormData,
    eventId,
    false,
    !EDITABLE_SELECTION_TYPES.includes(atlSelectionType)
  );

  if (appraiserUnavailableError || availableAtlsError) {
    return <ApolloError />;
  }

  return (
    <div>
      {settings.sowSettings?.canEdit && (
        <SowStatusSectionWrapper
          eventFormData={eventFormData}
          organizations={organizations}
          validationErrors={validationErrors}
          setEventFormData={setEventFormData}
          availableAtlList={availableAtlList}
          loading={eventLoading || orgUnitsLoading || availableAtlsLoading}
        />
      )}

      <section className='page-section'>
        <OrgOrgUnitSelection
          organizations={organizations}
          selectedOrgId={storedEventData?.OrgId}
          selectedOrgUnitIds={eventFormData.orgUnitIds}
          setSelections={setEventFormData}
          loading={eventLoading || orgUnitsLoading}
          displayOnlyUnitSelector={true}
          disableOrgUnitSelector={!settings.isOrgUnitSelectorEnabled}
        />
        <EventStatusWarnings
          showAppraisalDetailStatus={settings.showAppraisalDetailStatus}
          warningTypes={storedEventData?.WarningTypes}
        />
      </section>
      {settings.showIntendedDates && (
        <EventDates
          selectedStartDate={eventFormData.intendedStartDate}
          selectedEndDate={eventFormData.intendedEndDate}
          loading={eventLoading}
          label='Intended Event Dates'
          name='eventIntendedDates'
          disabled={true}
        />
      )}
      <section className='page-section'>
        <EventDates
          selectedStartDate={eventFormData.startDate}
          selectedEndDate={eventFormData.endDate}
          loading={eventLoading}
          onDateRangeChanged={getOnDateRangeChangedHandler('startDate', 'endDate')}
          excludedDateIntervals={excludedDateIntervals}
          intendedStartDate={
            storedEventData?.EventFlags?.includes(EVENT_FLAGS.FIRST_APPRAISAL_FOR_UNITS)
              ? null
              : storedEventData?.IntendedStartDate
          }
          minDate={getToday()}
          disabled={isEditDisabled || disableDateEdit || appraiserUnavailableLoading}
          label='Event Dates'
          name='eventDates'
        />
        {settings.canRequestDateChange && (
          <ChangeDateRequest
            excludedDateIntervals={excludedDateIntervals}
            status={dateRequestStatus}
            onSubmit={handleSubmitDataRequest}
            loading={eventLoading}
          />
        )}
      </section>
      <AtlSelection
        atlList={availableAtlList}
        eventFormData={eventFormData}
        setSelections={setEventFormData}
        loading={eventLoading}
        availableAtlsLoading={availableAtlsLoading}
        eventATLExternalId={storedEventData?.Atl?.ExternalUserId}
        selectionType={atlSelectionType}
        isEditDisabled={isEditDisabled}
        validationErrors={validationErrors}
      />

      {atlSelectionType === ATL_SELECTION_TYPE.ATL_PREFERENCES && (
        <AtlPreferenceTable
          atlPreferences={eventFormData.atlPreferences}
          atlSponsorRankings={eventFormData.atlSponsorRankings}
          setSelections={setEventFormData}
          loading={eventLoading}
        />
      )}

      <EventTeamTables
        eventTeams={eventFormData.eventTeams}
        setEventFormData={setEventFormData}
        loading={eventLoading}
        eventTeamErrors={validationErrors.eventTeams}
        isDisabledTeamTables={!settings.canEditTeamAndTravel}
        hideNotesColumn={isAtmView}
        showEventAssignment={settings.showEventAssignment}
        eventAssignments={filteredAssignmentData}
        eventType={eventFormData.eventType}
        showAssignmentRank={settings.showAssignmentRank}
        canAssignATMs={settings.canAssignATMs}
      />
      <TravelInformation
        logisticalNotes={eventFormData.logisticalNotes}
        deliveryMethod={eventFormData.deliveryMethod}
        regions={eventFormData.regions}
        setEventFormData={setEventFormData}
        validationErrors={validationErrors}
        loading={eventLoading}
      />
    </div>
  );
};

export default AppraiserEditEvent;
